import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import React from "react";
import { Link } from "tsi-common-react/src/components/Link/index";

import {
    ImageChooserBlock,
    RichNavigationBannerContentAssembly,
    RichNavigationLinkCollection as RichNavigationLinkCollectionValue,
    RichNavigationLinkTypes,
} from "@reactivated";

import {
    StreamField,
    StreamFieldBlockMap,
} from "@thelabnyc/thelabui/src/components/StreamField";
import {
    classNames,
    concatClassNames,
    getStyleVariantClass,
} from "@thelabnyc/thelabui/src/utils/styles";
import { IntrinsicElementProps } from "@thelabnyc/thelabui/src/utils/types";

import { useRichNavViewport } from "../../hooks/useRichNavViewport";
import { ISafeHTML, WebPageURL, isoWebPageURL } from "../../models/nominals";
import {
    RichNavigationVariantOptions,
    TRichNavigationVariantOptions,
    linkCollectionVariantClass,
} from "../../models/rich-navigation";
import {
    LinkCollection,
    bannerContentAssemblyComponents,
} from "../RichNavigation";
import { RichNavigationBannerLink } from "../RichNavigationBannerLink";
import { RichNavigationButtonLink } from "../RichNavigationButtonLink";
import { RichNavigationCalloutLink } from "../RichNavigationCalloutLink";
import { RichNavigationDynamicProductLink } from "../RichNavigationDynamicProductLink";
import { RichNavigationGeneralLink } from "../RichNavigationGeneralLink";
import { RichNavigationImageLink } from "../RichNavigationImageLink";
import { RichNavigationOfferLink } from "../RichNavigationOfferLink";
import { RichNavigationProductLink } from "../RichNavigationProductLink";
import { RichNavigationImagePromo } from "../RichNavigationPromoFeatures";
import { RichText } from "../RichText";

import sharedStyles from "../RichNavigationLink/shared.module.scss";
import styles, { Styles } from "./index.module.scss";

const richNavigationLinkTypeComponents: StreamFieldBlockMap<RichNavigationLinkTypes> =
    {
        rich_nav_general_link: RichNavigationGeneralLink,
        rich_nav_offer_link: RichNavigationOfferLink,
        rich_nav_product_link: RichNavigationProductLink,
        rich_nav_dynamic_product_link: RichNavigationDynamicProductLink,
        rich_nav_image_link: RichNavigationImageLink,
        rich_nav_banner_link: RichNavigationBannerLink,
        rich_nav_button_link: RichNavigationButtonLink,
        rich_nav_callout_link: RichNavigationCalloutLink,
    };

type StyleVariants<T extends string> = Record<T, string>;

interface ICollectionProps {
    value: RichNavigationLinkCollectionValue;
    attrs?: IntrinsicElementProps<HTMLElement>;
}

export interface RichNavigationLinkCollectionContentProps
    extends ICollectionProps {
    isCurrentCollectionOpen: boolean;
    mobileContentIndex?: number;
    /** Override auto-generated ids when the label/trigger lives in a parent. */
    labelId?: string;
    menuId?: string;
    /** Prefix from RichNavigation useId() so ids stay unique across instances. */
    mobileA11yIdPrefix?: string;
    /** When the CTA is rendered in a collection header, hide the inline duplicate. */
    suppressCta?: boolean;
    enableMobileAccordion?: boolean;
    /** Render image promos at every width, not only above `$small`. */
    showPromosAtAllWidths?: boolean;
}

interface WrapperProps {
    collection: ICollectionProps;
    banner_content: RichNavigationBannerContentAssembly | null;
    value_props?: {
        value_prop: {
            title: {
                html: string | ISafeHTML;
            };
            icon: {
                block: ImageChooserBlock;
            } | null;
        };
        link: {
            url: string;
        } | null;
    }[];
    openMobileContent: LinkCollection[];
    setOpenMobileContent: React.Dispatch<
        React.SetStateAction<LinkCollection[]>
    >;
    mobileContentIndex: number;
    enableMobileAccordion?: boolean;
    mobileA11yIdPrefix?: string;
}

interface IMenuTrigger {
    children: React.ReactNode;
    setOpenMobileContent: React.Dispatch<
        React.SetStateAction<LinkCollection[]>
    >;
    mobileContentIndex: number;
    enableMobileAccordion: boolean;
}

interface IValuePropItem {
    iconUrl: string | undefined;
    titleHtml: string | ISafeHTML;
    linkUrl: string | undefined;
}

interface IValueProps {
    value_props: WrapperProps["value_props"];
    enableValuePropsOnMobile?: boolean;
}

const getRichNavigationLinkCollectionMobileA11y = (
    mobileA11yIdPrefix: string,
    mobileContentIndex: number,
    enableMobileAccordion: boolean,
    belowMobile: boolean,
) => {
    const useMobileAccordion = enableMobileAccordion && belowMobile;
    return {
        labelId: `${mobileA11yIdPrefix}-label-${mobileContentIndex}`,
        menuId: `${mobileA11yIdPrefix}-menu-${mobileContentIndex}`,
        useMobileAccordion,
    };
};

const ValuePropItem = ({ iconUrl, titleHtml, linkUrl }: IValuePropItem) => {
    const content = (
        <>
            {iconUrl && <img src={iconUrl} alt="" />}
            {titleHtml && <RichText inline html={titleHtml} />}
        </>
    );

    return linkUrl ? <a href={linkUrl}>{content}</a> : content;
};

export const ValueProps = (props: IValueProps) => {
    const viewport = useRichNavViewport();
    // Value check
    if (!props.value_props || props.value_props.length <= 0) {
        return null;
    }
    // Property based check
    if (!props.enableValuePropsOnMobile && viewport.belowMobile) {
        return null;
    }
    return (
        <ul className={styles.valueProps}>
            {props.value_props.map((item, i) => (
                <li key={i}>
                    <ValuePropItem
                        iconUrl={item.value_prop.icon?.block.url}
                        titleHtml={item.value_prop.title?.html}
                        linkUrl={item.link?.url}
                    />
                </li>
            ))}
        </ul>
    );
};

const MenuTrigger = (props: IMenuTrigger) => {
    const viewport = useRichNavViewport();

    const onToggleAccordion = () => {
        props.setOpenMobileContent((prev) => {
            if (!props.enableMobileAccordion) {
                return prev;
            }
            return prev.map((accordion) => {
                // If it's the clicked accordion, toggle it
                if (accordion.id === props.mobileContentIndex) {
                    return { ...accordion, isOpen: !accordion.isOpen };
                }
                // Close all other accordions
                return { ...accordion, isOpen: false };
            });
        });
    };

    if (viewport.belowMobile) {
        return <div onClick={onToggleAccordion}>{props.children}</div>;
    }
    return (
        <NavigationMenu.Trigger
            className={concatClassNames([
                sharedStyles.hoverEffect,
                styles.trigger,
            ])}
        >
            {props.children}
        </NavigationMenu.Trigger>
    );
};

const MenuContent = (props: {
    children: React.ReactNode;
    hasBannerContent: boolean;
}) => {
    const viewport = useRichNavViewport();
    if (viewport.belowMobile) {
        return <>{props.children}</>;
    }
    return (
        <NavigationMenu.Content
            className={classNames([
                [styles.content, true],
                [styles.hasBannerContent, props.hasBannerContent],
            ])}
        >
            {props.children}
        </NavigationMenu.Content>
    );
};

export const RichNavigationLinkCollection = (props: WrapperProps) => {
    const viewport = useRichNavViewport();
    const generatedA11yIdPrefix = React.useId();
    const mobileA11yIdPrefix =
        props.mobileA11yIdPrefix ?? generatedA11yIdPrefix;
    const isCurrentCollectionOpen =
        props.openMobileContent[props.mobileContentIndex].isOpen;
    const linkClasses = classNames([
        [styles.link, true],
        [props.collection.value.collection_name_style?.style, true],
        [styles.linkMobileOpen, isCurrentCollectionOpen],
    ]);
    const { labelId, menuId, useMobileAccordion } =
        getRichNavigationLinkCollectionMobileA11y(
            mobileA11yIdPrefix,
            props.mobileContentIndex,
            props.enableMobileAccordion ?? false,
            viewport.belowMobile,
        );

    const mapRichNavigationVariants = (
        styles: Styles,
    ): StyleVariants<TRichNavigationVariantOptions> => {
        return {
            [RichNavigationVariantOptions.TWO_THIRDS_LINK]:
                styles.richNavigationLinkCollectionTwoThirdsLink,
            [RichNavigationVariantOptions.SINGLE_COLUMN]:
                styles.richNavigationLinkCollectionSingleColumn,
            [RichNavigationVariantOptions.GRID_PROMO]:
                styles.richNavigationLinkCollectionGridPromo,
            [RichNavigationVariantOptions.HEART]:
                styles.richNavigationLinkCollectionHeart,
            [RichNavigationVariantOptions.NO_TITLE]:
                styles.richNavigationLinkCollectionNoTitle,
            [RichNavigationVariantOptions.INSPIRATION]:
                styles.richNavigationLinkCollectionInspiration,
            [RichNavigationVariantOptions.DEFAULT]: "",
        };
    };

    const variant =
        props.collection.value.variant ?? RichNavigationVariantOptions.DEFAULT;
    const styleVariants = mapRichNavigationVariants(styles);

    return (
        <section
            className={concatClassNames([
                "rich-nav-link-collection",
                getStyleVariantClass(styleVariants, variant),
                variant,
            ])}
        >
            <MenuTrigger
                setOpenMobileContent={props.setOpenMobileContent}
                mobileContentIndex={props.mobileContentIndex}
                enableMobileAccordion={props.enableMobileAccordion ?? false}
            >
                {useMobileAccordion ? (
                    <h2>
                        <button
                            type="button"
                            className={linkClasses}
                            id={labelId}
                            aria-expanded={isCurrentCollectionOpen}
                            aria-controls={menuId}
                        >
                            {props.collection.value.name}
                        </button>
                    </h2>
                ) : (
                    <h2 className={linkClasses} id={labelId}>
                        {props.collection.value.name}
                    </h2>
                )}
            </MenuTrigger>
            <MenuContent
                hasBannerContent={
                    props.banner_content
                        ? props.banner_content.length > 0
                        : false
                }
            >
                <RichNavigationLinkCollectionContent
                    {...props.collection}
                    isCurrentCollectionOpen={isCurrentCollectionOpen}
                    mobileContentIndex={props.mobileContentIndex}
                    enableMobileAccordion={props.enableMobileAccordion}
                    mobileA11yIdPrefix={mobileA11yIdPrefix}
                />
                <ValueProps value_props={props.value_props} />
                {props.banner_content && (
                    <div className={styles.bannerWrapper}>
                        <StreamField
                            tagName={React.Fragment}
                            components={bannerContentAssemblyComponents}
                            value={props.banner_content}
                        />
                    </div>
                )}
            </MenuContent>
        </section>
    );
};

export const RichNavigationLinkCollectionContent = (
    props: RichNavigationLinkCollectionContentProps,
) => {
    const viewport = useRichNavViewport();
    const mobileContentIndex = props.mobileContentIndex ?? 0;
    const generatedA11yIdPrefix = React.useId();
    const mobileA11yIdPrefix =
        props.mobileA11yIdPrefix ?? generatedA11yIdPrefix;
    const {
        labelId: generatedLabelId,
        menuId: generatedMenuId,
        useMobileAccordion,
    } = getRichNavigationLinkCollectionMobileA11y(
        mobileA11yIdPrefix,
        mobileContentIndex,
        props.enableMobileAccordion ?? false,
        viewport.belowMobile,
    );
    const labelId = props.labelId ?? generatedLabelId;
    const menuId = props.menuId ?? generatedMenuId;
    const hasActivePromo =
        props.value.promo_features?.some(
            (feature) =>
                !feature.value.promo_period ||
                feature.value.promo_period.is_promo_active,
        ) ?? false;
    const wrapperClass = hasActivePromo
        ? styles.promoFeatureWrapper
        : styles.defaultWrapper;
    const variant = linkCollectionVariantClass(props.value.variant);

    const links = props.value.links ?? [];
    const hasLinks = links.length > 0;
    const ctaLinkUrl: WebPageURL | null = props.value.cta_link?.url
        ? isoWebPageURL.wrap(props.value.cta_link.url)
        : null;
    const ctaLabel =
        props.value.cta_text || props.value.cta_link?.title || null;
    const showCta =
        !props.suppressCta && Boolean(ctaLabel && props.value.cta_link?.url);
    const promoBlocks =
        (props.showPromosAtAllWidths || viewport.aboveSmall) &&
        props.value.promo_features
            ? props.value.promo_features.filter(
                  (block) =>
                      block.type === "image_promo" &&
                      (!block.value.promo_period ||
                          block.value.promo_period.is_promo_active),
              )
            : [];

    const isEmpty = !hasLinks && !showCta && promoBlocks.length === 0;
    // Keep accordion panels mounted when collapsed so aria-controls stays valid.
    const isHidden =
        viewport.belowMobile &&
        !props.isCurrentCollectionOpen &&
        !useMobileAccordion;
    if (isEmpty || isHidden) {
        return null;
    }

    return (
        <div
            className={concatClassNames([wrapperClass, variant])}
            id={menuId}
            aria-labelledby={useMobileAccordion ? labelId : props.labelId}
            aria-hidden={
                useMobileAccordion ? !props.isCurrentCollectionOpen : undefined
            }
            hidden={useMobileAccordion && !props.isCurrentCollectionOpen}
        >
            {(hasLinks || showCta) && (
                <div
                    className={concatClassNames([
                        styles.linkWrapper,
                        "rich-nav-link-wrapper",
                        variant,
                    ])}
                >
                    {links.map((link, i) => (
                        <StreamField
                            key={i}
                            tagName={React.Fragment}
                            components={richNavigationLinkTypeComponents}
                            value={link.value}
                        />
                    ))}
                    {showCta && (
                        <Link
                            className={styles.ctaLink}
                            href={ctaLinkUrl}
                            aria-label={ctaLabel ?? undefined}
                            target="_top"
                        >
                            {ctaLabel}
                        </Link>
                    )}
                </div>
            )}
            {promoBlocks.map((block) => (
                <RichNavigationImagePromo
                    key={block.id}
                    className={concatClassNames([
                        styles.imagePromo,
                        "rich-nav-image-promo",
                    ])}
                    value={block.value}
                />
            ))}
        </div>
    );
};
